import { useTonConnectUI, useTonWallet } from "@tonconnect/ui-react"
import clsx from "clsx"
import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { spaceNumber } from "@/helpers/spaceNumber"
import { getTgWebApp } from "@/helpers/tgWebApp"
import { showToast } from "@/helpers/toast"
import { useGameMoneyEnough } from "@/hooks/useGameMoneyEnough"
import { useModal } from "@/modals"
import { TonConnectProvider } from "@/providers/TonConnectProvider"
import { useAppSelector } from "@/store"
import { CARD_GAME_CURRENCY } from "@/store/apis/balanceAPI"
import {
  CARD_SKIN_TYPES,
  useGetCardBackTemplateByTypeQuery
} from "@/store/apis/cardBacksAPI"
import {
  IStarPaymentInvoice,
  ITonPaymentInvoice,
  STORE_CURRENCY,
  useCreateInvoiceMutation
} from "@/store/apis/storePaymentsAPI"
import { AppSelectors } from "@/store/slices/app"
import { Button } from "@/ui/Button"
import { CardView } from "@/ui/Card"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./BuyGoldCard.module.scss"

const modalType = "buyGoldCard"
const quantityOptions = [1, 5, 10, 30, 100] as const

export const BuyGoldCardComp: FC = () => {
  const { t } = useTranslation()
  const { t: tCard, ready: tCardReady } = useTranslation("cards")
  const { modal, getModalParams } = useModal(modalType)
  const [tonConnectUI] = useTonConnectUI()
  const senderWallet = useTonWallet()

  const storeCurrency = useAppSelector(AppSelectors.selectStoreCurrency())

  const [selectedQuantity, setSelectedQuantity] = useState<number>(1)

  const { data: goldCardTemplate } = useGetCardBackTemplateByTypeQuery(
    CARD_SKIN_TYPES.GOLD
  )

  const [createInvoice, { isLoading: isCreatingInvoice }] =
    useCreateInvoiceMutation()

  const { _id, type, data: cardData, pricing } = getModalParams(modalType)

  const resolvedCurrency = useMemo(() => {
    if (storeCurrency === STORE_CURRENCY.TG_STARS && pricing.isStarsEnabled) {
      return STORE_CURRENCY.TG_STARS
    }

    if (storeCurrency === STORE_CURRENCY.EXTERNAL_TON && pricing.isTonEnabled) {
      return STORE_CURRENCY.EXTERNAL_TON
    }

    if (
      storeCurrency === STORE_CURRENCY.GOLDEN_DUST &&
      pricing.isGoldenDustEnabled
    ) {
      return STORE_CURRENCY.GOLDEN_DUST
    }

    return null
  }, [pricing.isStarsEnabled, pricing.isTonEnabled, storeCurrency])

  const totalPrice = useMemo(() => {
    if (resolvedCurrency === STORE_CURRENCY.GOLDEN_DUST) {
      return pricing.goldenDust * selectedQuantity
    }

    if (resolvedCurrency === STORE_CURRENCY.EXTERNAL_TON) {
      return pricing.ton * selectedQuantity
    }

    return pricing.stars * selectedQuantity
  }, [pricing.stars, pricing.ton, resolvedCurrency, selectedQuantity])

  const isMoneyEnough = useGameMoneyEnough(
    totalPrice,
    CARD_GAME_CURRENCY.GOLDEN_DUST
  )

  const PriceIcon = useMemo(() => {
    if (resolvedCurrency === STORE_CURRENCY.EXTERNAL_TON) {
      return CurrencyIcon.ton
    }

    if (resolvedCurrency === STORE_CURRENCY.GOLDEN_DUST) {
      return CurrencyIcon.goldenDust
    }

    return CurrencyIcon.star
  }, [resolvedCurrency])

  const handleClose = () => {
    modal.close()
  }

  const handleSelectQuantity = (value: number) => {
    setSelectedQuantity(value)
  }

  const handleBuy = async () => {
    if (!resolvedCurrency) {
      showToast.error(t("modals.buyGoldCard.purchaseUnavailable"))
      return
    }

    try {
      if (resolvedCurrency === STORE_CURRENCY.TG_STARS) {
        const invoice = (await createInvoice({
          itemId: _id,
          itemType: type,
          currency: resolvedCurrency,
          amount: selectedQuantity,
          price: pricing.stars
        }).unwrap()) as IStarPaymentInvoice

        getTgWebApp().openInvoice(invoice.invoiceLink, () => undefined)
        modal.close()
        return
      } else if (resolvedCurrency === STORE_CURRENCY.EXTERNAL_TON) {
        if (!senderWallet?.account?.address) {
          tonConnectUI.openModal()
          return
        }

        const invoice = (await createInvoice({
          itemId: _id,
          itemType: type,
          currency: resolvedCurrency,
          amount: selectedQuantity,
          price: pricing.ton
        }).unwrap()) as ITonPaymentInvoice

        const transaction = {
          validUntil: invoice.validUntil,
          messages: [
            {
              address: invoice.to,
              amount: invoice.amount,
              payload: invoice.payload
            }
          ]
        }

        await tonConnectUI.sendTransaction(transaction, {
          modals: "all",
          notifications: "all"
        })
      } else if (resolvedCurrency === STORE_CURRENCY.GOLDEN_DUST) {
        if (!isMoneyEnough) {
          showToast.warning(t("modals.buyGoldCard.notEnoughMoney"))
          return
        }

        await createInvoice({
          itemId: _id,
          itemType: type,
          currency: resolvedCurrency,
          amount: selectedQuantity,
          cardId: cardData?._id,
          price: pricing.goldenDust
        }).unwrap()
      }

      modal.close()
    } catch {
      showToast.error(t("message.somethingWentWrong"))
    }
  }

  const cardTitle = useMemo(() => {
    return tCard(`card.${cardData?.type}.title.nominative`)
  }, [cardData?.type, tCard, tCardReady])

  const cardDescription = useMemo(() => {
    return tCard(`card.${cardData?.type}.description_short`)
  }, [cardData?.type, tCard, tCardReady])

  const text = useMemo(() => {
    if ((cardData?.maxUsesPerGame || Infinity) === 1) {
      return t(`cardCoolDown.oncePerMatch`, {
        maxUsesPerGame: cardData?.maxUsesPerGame
      })
    } else if ((cardData?.coolDownMovements || 0) > 1) {
      return t(
        `cardCoolDown.${(cardData?.maxUsesPerGame || 999) === 1 ? "oncePerMatch" : "coolDown"}`,
        {
          coolDownMovements: cardData?.coolDownMovements,
          turns: t("turn", {
            count: cardData?.maxUsesPerGame
          })
        }
      )
    }

    return
  }, [cardData, t])

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.BuyGoldCard)}
      body={{ className: styles.BuyGoldCard_body }}
    >
      <Modal.StoneFlexContainer
        className={styles.BuyGoldCard_container}
        content={{ className: styles.BuyGoldCard_content }}
        outside={
          <div className={clsx(styles.BuyGoldCard_footerButtons)}>
            <Button
              className={clsx(styles.BuyGoldCard_footerButtons_item)}
              styles="green"
              onClick={handleClose}
            >
              {t("button.close")}
            </Button>
            <Button
              className={clsx(
                styles.BuyGoldCard_footerButtons_item,
                styles.BuyGoldCard_footerButtons_item_buy
              )}
              onClick={handleBuy}
              isLoading={isCreatingInvoice}
              disabled={!resolvedCurrency}
            >
              <span className={clsx(styles.BuyGoldCard_buyContent)}>
                <PriceIcon />
                {spaceNumber(totalPrice)}
              </span>
            </Button>
          </div>
        }
      >
        <OutlinedText className={clsx(styles.BuyGoldCard_title)}>
          {t("modals.buyGoldCard.title")}
        </OutlinedText>

        <div className={clsx(styles.BuyGoldCard_top)}>
          <div className={clsx(styles.BuyGoldCard_preview)}>
            <CardView
              className={clsx(styles.BuyGoldCard_previewCard)}
              data={{ imgSrc: cardData?.imgSrc, price: 0 }}
              title={cardTitle}
              description={cardDescription}
              skin={goldCardTemplate}
              isShowBackSide={false}
              isMatchActive={true}
            />
          </div>

          <div className={clsx(styles.BuyGoldCard_info)}>
            <div className={clsx(styles.BuyGoldCard_info_top)}>
              <div className={clsx(styles.BuyGoldCard_name)}>{cardTitle}</div>
              <OutlinedText
                className={clsx(styles.BuyGoldCard_description, {
                  [styles[cardData?.type || ""]]: !!cardData
                })}
              >
                {cardDescription}
              </OutlinedText>
              <div className={clsx(styles.BuyGoldCard_text)}>{text}</div>
            </div>
            <div className={clsx(styles.BuyGoldCard_info_bottom)}>
              <div className={clsx(styles.BuyGoldCard_quantityTitle)}>
                {t("modals.buyGoldCard.quantityTitle")}
              </div>
              <div className={clsx(styles.BuyGoldCard_quantityList)}>
                {quantityOptions.map((value) => (
                  <div
                    key={value}
                    className={clsx(styles.BuyGoldCard_quantityItem, {
                      [styles.BuyGoldCard_quantityItem_selected]:
                        selectedQuantity === value
                    })}
                    onClick={() => handleSelectQuantity(value)}
                  >
                    x{value}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}

export const BuyGoldCard = () => {
  return (
    <TonConnectProvider>
      <BuyGoldCardComp />
    </TonConnectProvider>
  )
}
